import { addPlaceholderToCache, aiTagIdFromName, useCustomerAiTags } from '@solinkcorp/ai-tags';
import { useUserSession } from '@solinkcorp/authentication';
import { useCallback } from 'react';
import { type Controller } from '../Controller';
import { type PredefinedLayout } from '../layout/predefinedLayouts';

/**
 * Seeds AI-tag placeholders for template tag names that the customer hasn't
 * persisted yet, and reuses the existing BE id for names that already match a
 * customer-saved tag. Without the name-match check, a synthetic id is minted
 * for a name that already exists on the API; `useAiTags` then drops the
 * placeholder (customer record wins by name), leaving the synthetic id
 * unresolvable in `useSaveAiTags` and triggering a duplicate POST with the
 * UUID as the name. On save, the existing `useSaveAiTags` + `remapAiTagIds`
 * pipeline creates the real customer tag rows from any remaining synthetic
 * ids — see [[vdw-ai-tag-create-on-save]].
 */
export function useStartLayoutFromTemplate(controller: Controller) {
  const user = useUserSession();
  const { data: customerSavedTags } = useCustomerAiTags();

  return useCallback(
    async (template: PredefinedLayout) => {
      const customerIdByName = new Map(
        (customerSavedTags ?? []).map((t) => [t.name.toLowerCase(), t.id] as const),
      );
      const resolveTagId = (name: string) =>
        customerIdByName.get(name.toLowerCase()) ?? aiTagIdFromName(name, user.customerId);

      const placeholders = template.widgets.flatMap((w) =>
        w.tagNames
          .filter((name) => !customerIdByName.has(name.toLowerCase()))
          .map((name) => ({ id: resolveTagId(name), name })),
      );
      await addPlaceholderToCache(placeholders, user);
      controller.startLayoutFromTemplate(template, resolveTagId);
    },
    [controller, user, customerSavedTags],
  );
}
